/**
 * Lake data loading (static JSON under public/data), conversion to ENU and the
 * LakeManager that tracks which lake the user is on.
 */
import type {
  LakeENU,
  LakeFile,
  LakeIndex,
  LakeIndexEntry,
  LatLon,
  LineCoords,
  LineENU,
  LocalFrame,
  MultiPolygonCoords,
  MultiPolygonENU,
  Position,
} from '../contracts';
import { createLocalFrame } from './frame';
import { prepareLake } from './query';

/** HUD strings (lakeMessage). */
export const LAKE_MESSAGES = {
  loading: 'Lade Seedaten…',
  unavailable: 'Seedaten nicht verfügbar',
  noLake: 'Kein See mit amtlichen Daten in der Nähe',
} as const;

export type FetchFn = (url: string) => Promise<Response>;

const defaultFetch: FetchFn = (url) => globalThis.fetch(url);

function joinUrl(base: string, path: string): string {
  return base.endsWith('/') ? base + path : `${base}/${path}`;
}

async function fetchJson(url: string, fetchFn: FetchFn): Promise<unknown> {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return res.json();
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export async function loadLakeIndex(baseUrl = '/data/', fetchFn: FetchFn = defaultFetch): Promise<LakeIndex> {
  const url = joinUrl(baseUrl, 'index.json');
  const j = await fetchJson(url, fetchFn);
  if (!isObj(j) || !Array.isArray(j.lakes)) throw new Error(`Invalid lake index: ${url}`);
  for (const l of j.lakes as unknown[]) {
    if (!isObj(l) || typeof l.id !== 'string' || typeof l.file !== 'string' || !Array.isArray(l.bbox) || l.bbox.length !== 4) {
      throw new Error(`Invalid lake index entry in ${url}`);
    }
  }
  return j as unknown as LakeIndex;
}

/** Fetch and validate a lake file. Missing geometry members default to empty. */
export async function loadLakeFile(baseUrl: string, entry: LakeIndexEntry, fetchFn: FetchFn = defaultFetch): Promise<LakeFile> {
  const url = joinUrl(baseUrl, entry.file);
  return normalizeLakeFile(await fetchJson(url, fetchFn), entry);
}

const MP_KEYS = ['inner', 'outer', 'extended', 'coverage', 'lakeOutline'] as const;
const LINE_KEYS = ['extendedLines', 'pierLines', 'osmPierLines', 'shoreLines', 'limitLines'] as const;

/** Validate the top-level shape of a parsed lake file and fill missing members with defaults. */
export function normalizeLakeFile(j: unknown, entry?: LakeIndexEntry): LakeFile {
  if (!isObj(j)) throw new Error('Invalid lake file: not an object');
  const meta = isObj(j.meta) ? j.meta : {};
  const out: Record<string, unknown> = {
    meta: {
      id: typeof meta.id === 'string' ? meta.id : (entry?.id ?? 'unknown'),
      name: typeof meta.name === 'string' ? meta.name : (entry?.name ?? ''),
      source: typeof meta.source === 'string' ? meta.source : '',
      attribution: typeof meta.attribution === 'string' ? meta.attribution : '',
      dataDate: typeof meta.dataDate === 'string' ? meta.dataDate : '',
    },
    extendedNote: typeof j.extendedNote === 'string' ? j.extendedNote : null,
  };
  for (const k of MP_KEYS) {
    const v = j[k] ?? [];
    if (!Array.isArray(v)) throw new Error(`Invalid lake file: ${k} is not an array`);
    out[k] = v;
  }
  for (const k of LINE_KEYS) {
    const v = j[k] ?? [];
    if (!Array.isArray(v)) throw new Error(`Invalid lake file: ${k} is not an array`);
    out[k] = v;
  }
  // OSM slipways (optional in older files); keep only well-formed entries.
  const sw = Array.isArray(j.slipways) ? j.slipways : [];
  out.slipways = sw.filter(
    (w: unknown) => isObj(w) && Number.isFinite(w.lat) && Number.isFinite(w.lon),
  );
  const pp = Array.isArray(j.osmPierPoints) ? j.osmPierPoints : [];
  out.osmPierPoints = pp.filter(
    (w: unknown) => isObj(w) && Number.isFinite(w.lat) && Number.isFinite(w.lon),
  );
  return out as unknown as LakeFile;
}

// ─── ENU conversion ─────────────────────────────────────────────────────────

function toFlat(coords: Position[], frame: LocalFrame, close: boolean): Float64Array {
  const n = coords.length;
  const needClose =
    close && n > 0 && (coords[0][0] !== coords[n - 1][0] || coords[0][1] !== coords[n - 1][1]);
  const out = new Float64Array((n + (needClose ? 1 : 0)) * 2);
  for (let i = 0; i < n; i++) {
    const p = frame.toENU({ lat: coords[i][1], lon: coords[i][0] });
    out[2 * i] = p.e;
    out[2 * i + 1] = p.n;
  }
  if (needClose) {
    out[2 * n] = out[0];
    out[2 * n + 1] = out[1];
  }
  return out;
}

function mpToENU(mp: MultiPolygonCoords, frame: LocalFrame): MultiPolygonENU {
  return mp.map((poly) => poly.map((ring) => toFlat(ring, frame, true)));
}

function linesToENU(lines: LineCoords[], frame: LocalFrame): LineENU[] {
  return lines.filter((l) => l.length > 0).map((l) => toFlat(l, frame, false));
}

/** Convert a lake file into flattened ENU geometry in the given frame. */
export function lakeToENU(file: LakeFile, frame: LocalFrame): LakeENU {
  return {
    meta: file.meta,
    frame,
    inner: mpToENU(file.inner, frame),
    outer: mpToENU(file.outer, frame),
    extended: mpToENU(file.extended, frame),
    extendedNote: file.extendedNote,
    extendedLines: linesToENU(file.extendedLines ?? [], frame),
    pierLines: linesToENU(file.pierLines ?? [], frame),
    slipways: (file.slipways ?? []).map((w) => {
      const p = frame.toENU({ lat: w.lat, lon: w.lon });
      return { e: p.e, n: p.n, name: w.name, osmId: w.osmId };
    }),
    osmPierLines: linesToENU(file.osmPierLines ?? [], frame),
    osmPierPoints: (file.osmPierPoints ?? []).map((w) => {
      const p = frame.toENU({ lat: w.lat, lon: w.lon });
      return { e: p.e, n: p.n, name: w.name, osmId: w.osmId };
    }),
    coverage: mpToENU(file.coverage, frame),
    lakeOutline: mpToENU(file.lakeOutline, frame),
    shoreLines: linesToENU(file.shoreLines, frame),
    limitLines: linesToENU(file.limitLines, frame),
  };
}

// ─── Lake selection ─────────────────────────────────────────────────────────

export function bboxCentre(bbox: LakeIndexEntry['bbox']): LatLon {
  return { lat: (bbox[1] + bbox[3]) / 2, lon: (bbox[0] + bbox[2]) / 2 };
}

export function bboxContains(bbox: LakeIndexEntry['bbox'], p: LatLon): boolean {
  return p.lon >= bbox[0] && p.lon <= bbox[2] && p.lat >= bbox[1] && p.lat <= bbox[3];
}

/** Crossing-number test in lon/lat degrees (fine for containment in small areas). */
function inMultiPolygonLonLat(mp: MultiPolygonCoords, p: LatLon): boolean {
  const x = p.lon;
  const y = p.lat;
  for (const poly of mp) {
    let inPoly = false;
    for (let r = 0; r < poly.length; r++) {
      const ring = poly[r];
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [x1, y1] = ring[j];
        const [x2, y2] = ring[i];
        if (y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) inside = !inside;
      }
      if (r === 0) {
        if (!inside) break;
        inPoly = true;
      } else if (inside) {
        inPoly = false;
        break;
      }
    }
    if (inPoly) return true;
  }
  return false;
}

/**
 * Lake for position p: the entry whose bbox contains p. If several bboxes contain p
 * (e.g. Vierwaldstättersee / Zugersee near Küssnacht), the lake whose loaded outline
 * contains p wins, then `preferId` (the current lake, hysteresis), then the nearest
 * bbox centre.
 */
export function pickLake(
  entries: readonly LakeIndexEntry[],
  p: LatLon,
  files?: ReadonlyMap<string, LakeFile>,
  preferId?: string | null,
): LakeIndexEntry | null {
  const cands = entries.filter((e) => bboxContains(e.bbox, p));
  if (cands.length <= 1) return cands[0] ?? null;
  if (files) {
    for (const c of cands) {
      const f = files.get(c.id);
      if (f && inMultiPolygonLonLat(f.lakeOutline, p)) return c;
    }
  }
  const pref = preferId ? cands.find((c) => c.id === preferId) : undefined;
  if (pref) return pref;
  const kx = Math.cos((p.lat * Math.PI) / 180);
  let best = cands[0];
  let bestD = Infinity;
  for (const c of cands) {
    const m = bboxCentre(c.bbox);
    const d = ((m.lon - p.lon) * kx) ** 2 + (m.lat - p.lat) ** 2;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

// ─── LakeManager ────────────────────────────────────────────────────────────

export interface LakeManagerOptions {
  /** Base URL of the data directory (default '/data/'). */
  baseUrl?: string;
  fetch?: FetchFn;
  /** Minimum time between retries of a failed download (ms, default 15000). */
  retryMs?: number;
  /** Clock for the retry backoff (default performance.now / Date.now). */
  now?: () => number;
}

/**
 * Tracks the lake at the user position. Call `update(p)` on every position fix
 * and `loadAll()` once at start. Downloads are serialized (one in flight) and
 * cached; failures are retried after `retryMs` on later updates.
 *
 * `current` is the lake at the last position (in its own frame, origin = index
 * bbox centre, stable for the lifetime of the manager) or null. `message` is the
 * HUD lake message or null.
 */
export class LakeManager {
  index: LakeIndex | null = null;
  current: LakeENU | null = null;
  /** All loaded lake files (index order), for the 2D map. */
  files: LakeFile[] = [];
  message: string | null = null;

  private readonly baseUrl: string;
  private readonly fetchFn: FetchFn;
  private readonly retryMs: number;
  private readonly now: () => number;
  private readonly fileCache = new Map<string, LakeFile>();
  private readonly enuCache = new Map<string, LakeENU>();
  private readonly pending = new Map<string, Promise<LakeFile | null>>();
  private readonly failedAt = new Map<string, number>();
  private indexPromise: Promise<LakeIndex | null> | null = null;
  private indexFailedAt: number | null = null;
  /** Serializes downloads. */
  private chain: Promise<unknown> = Promise.resolve();
  private lastPos: LatLon | null = null;
  private wanted: LakeIndexEntry | null = null;
  private readonly listeners = new Set<(m: LakeManager) => void>();
  private emittedFiles: LakeFile[] = this.files;

  constructor(opts: LakeManagerOptions = {}) {
    this.baseUrl = opts.baseUrl ?? '/data/';
    this.fetchFn = opts.fetch ?? defaultFetch;
    this.retryMs = opts.retryMs ?? 15000;
    this.now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  }

  onChange(cb: (m: LakeManager) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /** Feed a new user position. Cheap; safe to call at GPS rate. */
  update(p: LatLon): void {
    this.lastPos = { lat: p.lat, lon: p.lon };
    if (!this.index) {
      void this.ensureIndex();
      this.refresh();
      return;
    }
    this.selectAndLoad();
  }

  /** Load the index and every lake file (sequentially). Resolves when all have loaded or failed. */
  async loadAll(): Promise<void> {
    const index = await this.ensureIndex();
    if (!index) return;
    for (const entry of index.lakes) await this.ensureFile(entry);
  }

  /** ENU geometry of a loaded lake (created on first use, cached with a stable frame). */
  lakeENU(id: string): LakeENU | null {
    let lake = this.enuCache.get(id);
    if (lake) return lake;
    const file = this.fileCache.get(id);
    const entry = this.index?.lakes.find((l) => l.id === id);
    if (!file || !entry) return null;
    lake = lakeToENU(file, createLocalFrame(bboxCentre(entry.bbox)));
    prepareLake(lake);
    this.enuCache.set(id, lake);
    return lake;
  }

  private ensureIndex(): Promise<LakeIndex | null> {
    if (this.index) return Promise.resolve(this.index);
    if (this.indexPromise) return this.indexPromise;
    if (this.indexFailedAt !== null && this.now() - this.indexFailedAt < this.retryMs) return Promise.resolve(null);
    const p = loadLakeIndex(this.baseUrl, this.fetchFn).then(
      (index) => {
        this.index = index;
        this.indexFailedAt = null;
        return index;
      },
      (err: unknown) => {
        console.warn('[geo] lake index failed:', err);
        this.indexFailedAt = this.now();
        return null;
      },
    );
    this.indexPromise = p;
    void p.then(() => {
      this.indexPromise = null;
      if (this.index && this.lastPos) this.selectAndLoad();
      else this.refresh();
    });
    return p;
  }

  private ensureFile(entry: LakeIndexEntry): Promise<LakeFile | null> {
    const cached = this.fileCache.get(entry.id);
    if (cached) return Promise.resolve(cached);
    const inflight = this.pending.get(entry.id);
    if (inflight) return inflight;
    const failed = this.failedAt.get(entry.id);
    if (failed !== undefined && this.now() - failed < this.retryMs) return Promise.resolve(null);
    const p = this.chain
      .then(() => loadLakeFile(this.baseUrl, entry, this.fetchFn))
      .then(
        (file) => {
          this.fileCache.set(entry.id, file);
          this.failedAt.delete(entry.id);
          this.files = (this.index?.lakes ?? [])
            .map((l) => this.fileCache.get(l.id))
            .filter((f): f is LakeFile => f !== undefined);
          return file;
        },
        (err: unknown) => {
          console.warn(`[geo] lake file ${entry.file} failed:`, err);
          this.failedAt.set(entry.id, this.now());
          return null;
        },
      );
    this.pending.set(entry.id, p);
    this.chain = p;
    void p.then(() => {
      this.pending.delete(entry.id);
      if (this.lastPos) this.selectAndLoad();
      else this.refresh();
    });
    return p;
  }

  private selectAndLoad(): void {
    if (!this.index || !this.lastPos) return;
    const p = this.lastPos;
    this.wanted = pickLake(this.index.lakes, p, this.fileCache, this.current?.meta.id ?? this.wanted?.id);
    if (this.wanted && !this.fileCache.has(this.wanted.id)) void this.ensureFile(this.wanted);
    // Overlapping bboxes are resolved with the lake outlines: fetch the other candidates too.
    for (const e of this.index.lakes) {
      if (e !== this.wanted && bboxContains(e.bbox, p) && !this.fileCache.has(e.id)) void this.ensureFile(e);
    }
    this.refresh();
  }

  /** Recompute `current` / `message`; notify listeners only if something changed. */
  private refresh(): void {
    const prevCurrent = this.current;
    const prevMessage = this.message;
    const w = this.wanted;
    this.current = w && this.index && this.lastPos ? this.lakeENU(w.id) : null;
    this.message = this.computeMessage();
    const filesChanged = this.files !== this.emittedFiles;
    this.emittedFiles = this.files;
    if (this.current !== prevCurrent || this.message !== prevMessage || filesChanged) this.emit();
  }

  private computeMessage(): string | null {
    if (!this.index) {
      if (this.indexFailedAt !== null) return LAKE_MESSAGES.unavailable;
      return this.lastPos ? LAKE_MESSAGES.loading : null;
    }
    if (!this.lastPos) return null;
    if (!this.wanted) return LAKE_MESSAGES.noLake;
    if (this.current) return null;
    if (this.failedAt.has(this.wanted.id) && !this.pending.has(this.wanted.id)) return LAKE_MESSAGES.unavailable;
    return LAKE_MESSAGES.loading;
  }

  private emit(): void {
    for (const cb of this.listeners) {
      try {
        cb(this);
      } catch (err) {
        console.error('[geo] LakeManager listener failed:', err);
      }
    }
  }
}
